import { sha256 } from 'js-sha256';
import _ from 'lodash';
import dayjs from 'dayjs';
import CustomParseFormat from 'dayjs/plugin/customParseFormat';
import AdvancedFormat from 'dayjs/plugin/advancedFormat';
import { v4 as uuidV4 } from 'uuid';
import { defineComponent } from 'vue';
import states from '~/assets/data/states.json';
import { PLAN_ENCODE_MAP } from '~/assets/data/plan-maps.json';
import policies from '~/assets/data/policies.json';
import LoadingOverlay from '~/components/common/LoadingOverlay.vue';
import ProgressIndicator from '~/components/common/ProgressIndicator.vue';
import FormPersonalInfo from '~/components/members-area/FormPersonalInfo.vue';
import FormPlanInfo from '~/components/members-area/FormPlanInfo.vue';
import PlanChangeModal from '~/components/members-area/PlanChangeModal.vue';
import AddressDropdown from '~/components/members-area/AddressDropdown.vue';
import MemberSurvey from '~/components/members-area/MemberSurvey.vue';
import PlanSection from '~/components/members-area/PlanSection.vue';
import PlaidSection from '~/components/members-area/PlaidSection.vue';
import DevMenu from '~/components/members-area/DevMenu.vue';
import DisclosureFooter from '~/components/members-area/DisclosureFooter.vue';
import TaskBox from '~/components/members-area/TaskBox.vue';
import PolicyUpdateSection from '~/components/members-area/PolicyUpdateSection.vue';
import ConfirmInfoSection from '~/components/members-area/ConfirmInfoSection.vue';
import PersonaSection from '~/components/members-area/PersonaSection.vue';
import DownloadBadges from '~/components/common/DownloadBadges.vue';
import AddressSuggestionModal from '~/components/members-area/AddressSuggestionModal.vue';
import AddressErrorModal from '~/components/members-area/AddressErrorModal.vue';
import DisclosureModal from '~/components/members-area/DisclosureModal.vue';
import PaymentOptionEducation from '~/components/members-area/PaymentOptionEducation.vue';
import ConfirmationScreen from '~/components/members-area/ConfirmationScreen.vue';
import MlaNoticeSection from '~/components/members-area/MlaNoticeSection.vue';
import SurveyScreen from '~/components/members-area/SurveyScreen.vue';
import BackupPaymentStage from '~/components/members-area/BackupPaymentStage.vue';
import ConfirmedScreen from '~/components/members-area/ConfirmedScreen.vue';
import FunnelError from '~/components/members-area/FunnelError.vue';
import FunnelWarning from '~/components/members-area/FunnelWarning.vue';
import ECBSVModal from '~/components/members-area/ECBSVModal.vue';
import smarty from '~/mixins/smarty';
import { EverflowEvents } from '~/utils/everflow';
import getPlans, { getPlanByName } from '~/utils/plans';
import { formatBirthday, getFormErrors, DEFAULT_ADDRESS, FORM_EMPTY } from '~/utils/form';
dayjs.extend(CustomParseFormat);
const DEFAULT_PLAN = {
  billingCycle: 0,
  selectedPlan: 1
};
const FORM_DEBUG = {
  first_name: 'Test',
  last_name: 'Account',
  email: 'roland-test-12@extra.app',
  phone: '213-459-1112',
  address: {
    street_one: '360 E 2nd St',
    street_two: 'Suite 800',
    zip_code: '90012',
    state: 'CA',
    city: 'Los Angeles'
  },
  date_of_birth: '01/01/2000',
  ssn: '111-11-1111'
};
const DEFAULT_FORM = _.cloneDeep(FORM_EMPTY);
export var PaymentOption;
(function (PaymentOption) {
  PaymentOption["NEXT_DAY_ACH"] = "next_day_ach";
  PaymentOption["FOURTEEN_DAYS_ACH"] = "14_days_ach";
})(PaymentOption || (PaymentOption = {}));
var STAGE;
(function (STAGE) {
  // please keep these in the same order as the actual components 🙂
  // the slugs on the right are what determine the URL
  STAGE["error"] = "error";
  STAGE["warning"] = "warning";
  STAGE["locked"] = "locked";
  STAGE["rejected"] = "rejected";
  STAGE["policyUpdate"] = "policy-update";
  STAGE["questions"] = "questions";
  STAGE["plan"] = "plan";
  STAGE["creditGoals"] = "credit-goals";
  // billing = 'billing-confirm', // unused
  // corporate account stage - unused
  STAGE["plaid"] = "plaid";
  // approved = 'approved', // unused
  STAGE["form"] = "form";
  STAGE["confirmInfo"] = "confirm-info";
  STAGE["persona"] = "persona";
  STAGE["pendingKYC"] = "pending-kyc";
  STAGE["confirmation"] = "confirmation";
  STAGE["mlaNotice"] = "mla-notice";
  STAGE["backupPayment"] = "backup-payment";
  STAGE["survey"] = "survey";
  STAGE["creditBand"] = "credit-band";
  STAGE["confirmed"] = "confirmed";
})(STAGE || (STAGE = {}));
export const sleep = time => new Promise(resolve => setTimeout(resolve, time));
export default defineComponent({
  name: 'Card',
  // middleware: 'members-auth',
  components: {
    LoadingOverlay,
    ProgressIndicator,
    MemberSurvey,
    PlanSection,
    PlaidSection,
    DevMenu,
    DisclosureFooter,
    TaskBox,
    DownloadBadges,
    PolicyUpdateSection,
    ConfirmInfoSection,
    PersonaSection,
    FormPersonalInfo,
    FormPlanInfo,
    PlanChangeModal,
    AddressDropdown,
    AddressSuggestionModal,
    AddressErrorModal,
    // DetailBox,
    // MessageBox,
    DisclosureModal,
    ECBSVModal,
    PaymentOptionEducation,
    ConfirmationScreen,
    MlaNoticeSection,
    SurveyScreen,
    BackupPaymentStage,
    ConfirmedScreen,
    FunnelError,
    FunnelWarning
  },
  mixins: [smarty],
  layout: 'members-area',
  data() {
    return {
      loaded: false,
      isDev: this.$config.environment === 'development' || this.$config.plaidEnv === 'sandbox',
      fromApp: false,
      plaidEnv: this.$config.plaidEnv || 'production',
      showPlaidTestCredentials: false,
      isDemo: false,
      states,
      stageOverride: null,
      stageLoading: false,
      processing: true,
      processingReasonManual: null,
      token: null,
      memberProfile: null,
      // user is in the EAS submit chain: addressAuthVerify -> softSubmitForm -> readyToSubmit -> tryFinish -> finish
      finishing: false,
      ecbsvStatus: null,
      ecbsvRemainder: null,
      kycStatus: null,
      kycSubmitted: false,
      error: null,
      // TODO type errors better
      errorContext: null,
      warningContext: null,
      lock: null,
      purchase: false,
      redirectTo: null,
      redirecting: false,
      submitPlanProcessing: false,
      easStatus: '',
      pendingEAS: false,
      decisionStatus: null,
      decisionPolling: false,
      decisionPollTimeout: undefined,
      kycStatusPolling: false,
      kycStatusPollTimeout: undefined,
      forcePendingKyc: false,
      canRetryPlaid: false,
      promptManualReview: false,
      partialData: false,
      formShowPlanChangeModal: false,
      formPaymentOptionEnabled: true,
      formPaymentOptionAgreed: false,
      showPaymentOptionEducation: false,
      paymentOption: PaymentOption.NEXT_DAY_ACH,
      formSubmitted: false,
      formProcessing: false,
      form: DEFAULT_FORM,
      formErrors: {
        address_street_one: false,
        address_street_two: false,
        address_city: false,
        address_state: false,
        address_zip_code: false,
        ssn: false,
        date_of_birth: false
      },
      formShowEmailTooltip: false,
      formStarted: false,
      planSubmitted: false,
      plan: DEFAULT_PLAN,
      planAutopay: true,
      allowAutopayChange: false,
      homepagePricingTestEnabled: false,
      disclosureFile: '',
      showDisclosure: false,
      handleEcbsvCheck: null,
      confirmation: true,
      mlaNoticeEnabled: true,
      mlaNoticeViewed: false,
      surveyFilled: false,
      billingConfirmed: false,
      seenResultScreen: false,
      // confirmedNext: false,
      isDateSupported: false,
      forcedFraudResult: null,
      forcedECBSVResult: null,
      hashCollision: false,
      collisionAttempts: 0,
      /** 0 = no CB, 1 = CB after conversion, 2 = CB before conversion */
      creditBandVariant: 0,
      creditGoalsSurveyAnswered: false,
      creditBandAnswered: false,
      d0Answered: false,
      code_redeemed: false,
      showError: '',
      traceId: '',
      // hey you, if you add new checkboxes, dont forget to allowlist them in the @extra/api code too! -roland
      formPolicyConsentTimestamps: {},
      confirmationPolicyConsentTimestamps: {},
      plaidSectionLayout: 0,
      plaidScriptLoaded: false,
      plaidProcessing: false,
      plaidAccessToken: null,
      plaidPublicToken: null,
      plaidAccountId: null,
      plaidMetadata: null,
      plaidRefreshToken: null,
      plaidInsufficient: null,
      ecbsvInvalid: false,
      promptECBSV: false,
      plaidAccountType: null,
      corporateAccountInfo: null,
      // remote states
      ecbsvHardRejection: false,
      kycComplete: false,
      agreementsComplete: false,
      promoCodeValue: '',
      codeSent: false,
      showForm: false,
      linkToken: '',
      cleanFunnel: false,
      conditionalAuthEnabled: true,
      ttdUrl: 'https://insight.adsrvr.org/track/pxl/?adv=fjfloca&ct=0:dnpm3u8&fmt=3',
      leadLOCEligibleStates: [],
      growthbookDynamicValues: {
        funnel_ecbsv_enabled_public: false,
        funnel_ecbsv_enabled_staff: false,
        funnel_ecbsv_modal: false,
        funnel_use_finish_controller: false
      },
      backupPaymentPrompt: false,
      backupPaymentOnFile: false
    };
  },
  head() {
    return {
      script: [{
        hid: 'socure-fprint',
        src: 'https://js.dvnfo.com/devicer.min.js',
        async: true
      }, {
        hid: 'sentilink-fprint',
        src: 'https://cdn.sentilink.com/fingerprinting/2.0.0/senti.js',
        async: true
      },
      // @ts-ignore
      {
        hid: 'plaid-',
        src: 'https://cdn.plaid.com/link/v2/stable/link-initialize.js',
        skip: this.plaidScriptLoaded || this.currentStage !== STAGE.plaid,
        callback() {
          this.plaidScriptLoaded = true;
        }
      }],
      bodyAttrs: {}
    };
  },
  computed: {
    _STAGE_enum() {
      return STAGE;
    },
    currentStage() {
      return this.processing || this.submitPlanProcessing ? 'loading' : this.stageOverride || _([
      // keep these in the same order as the actual components or the stage could display incorrectly
      this.showLockStage && STAGE.locked, this.showErrorStage && STAGE.error, this.showFunnelWarningStage && STAGE.warning, this.decisionFinalRejected && STAGE.rejected, this.showPolicyUpdateStage && STAGE.policyUpdate,
      // TODO maybe time to remove this now that we have two other surveys lol
      this.showQuestionsStage && STAGE.questions, this.showCreditGoalsStage && STAGE.creditGoals, this.showPlanStage && STAGE.plan,
      // this.showBillingStage && STAGE.billing, // unused
      this.showPlaidStage && STAGE.plaid,
      // this.showResultScreen && STAGE.approved, // unused
      this.showFormStage && STAGE.form, this.showConfirmInfoStage && STAGE.confirmInfo, this.showPersonaStage && STAGE.persona, this.showConfirmationScreenStage && STAGE.confirmation, this.showMlaNoticeStage && STAGE.mlaNotice, this.showCreditBandStage && STAGE.creditBand, this.showBackupPaymentStage && STAGE.backupPayment, this.showSurveyScreenStage && STAGE.survey, this.showConfirmedStage && STAGE.confirmed]).filter(e => e).first();
    },
    showPolicyUpdateStage() {
      var _a;
      return (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.policy_update_required;
    },
    showQuestionsStage() {
      // return !this.d0Answered
      return false;
    },
    showCreditGoalsStage() {
      return !this.creditGoalsSurveyAnswered || this.creditBandVariant === 2 && !this.creditBandAnswered;
    },
    showPlanStage() {
      return !this.planSubmitted || !this.plan;
    },
    // showBillingStage() {
    // 	return !this.showPlanStage && !this.billingConfirmed
    // },
    showCorporateAccountStage() {
      return this.plaidAccountType === 'corporate';
    },
    showPlaidStage() {
      return !this.plaidReady && !this.plaidRemoteReady;
    },
    showFormStage() {
      return !this.formSubmitted && !this.formFilled || !this.kycSubmitted;
    },
    showConfirmInfoStage() {
      var _a;
      return (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.loan_application_expired;
    },
    showPersonaStage() {
      var _a;
      // leaving !this.memberProfile?.document_portal.incomplete_locked_kyc as it is a good bypass for Persona for now (CAU Critical flow)
      if (this.forcePendingKyc || this.kycComplete || !((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.document_portal.incomplete_locked_kyc)) return false;
      return !this.personaSubmitted && !this.decisionFinalRejected || !this.kycComplete;
    },
    showFunnelWarningStage() {
      // Probably not the cleanest approach
      // but getEasStatus will set the warning context
      // for proper warning stage evaluation
      this.getEasStatus();
      return this.warningContext && !this.processing;
    },
    showConfirmationScreenStage() {
      var _a;
      return this.kycComplete && this.kycStatus === 'PASSED' && (this.personaApproved || !((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.document_portal.kyc_required_docs)) && !this.agreementsComplete && !this.decisionFinalRejected;
    },
    showMlaNoticeStage() {
      var _a;
      return this.mlaNoticeEnabled && this.agreementsComplete && ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.mla_covered) && !this.mlaNoticeViewed && !this.surveyFilled;
    },
    showCreditBandStage() {
      return this.creditBandVariant === 1 && !(this.creditGoalsSurveyAnswered && this.creditBandAnswered);
    },
    showBackupPaymentStage() {
      return this.agreementsComplete && this.backupPaymentPrompt && !this.backupPaymentOnFile;
    },
    showSurveyScreenStage() {
      return this.agreementsComplete && this.showBackupPaymentStage && !this.surveyFilled;
    },
    showConfirmedStage() {
      return this.agreementsComplete && this.kycStatus === 'PASSED' && !this.decisionFinalRejected;
    },
    // watch and kick to /error
    showErrorStage() {
      console.log('showErrorStage', this.error, this.errorContext);
      return (this.error || this.errorContext) && !this.processing && !this.waitingForDecision;
    },
    showLockStage() {
      return !!this.lock;
    },
    plans() {
      return getPlans(this.alternateFunnel);
    },
    planSelected() {
      return this.plan && this.plan.billingCycle > -1 && this.plan.selectedPlan > -1;
    },
    plaidReady() {
      return this.plaidAccountId && (this.plaidAccessToken || this.plaidPublicToken || '').includes(this.plaidEnv);
    },
    plaidRemoteReady() {
      return this.memberProfile && this.memberProfile.bank_linked;
    },
    formPolicies: {
      get() {
        return Object.keys(this.formPolicyConsentTimestamps);
      },
      set(newValue) {
        // remove unchecked policies
        const checkedPolicies = _.pick(this.formPolicyConsentTimestamps, newValue);
        // add newly checked policies
        newValue.forEach(key => {
          if (!checkedPolicies[key]) checkedPolicies[key] = new Date();
        });
        this.formPolicyConsentTimestamps = checkedPolicies;
      }
    },
    formFilled() {
      var _a;
      const iterables = _.concat(_.values(this.form), _.values(_.omit(this.form.address, 'street_two')), this.formattedBirthday, _.map(_.values(this.formErrors), v => !v ? 'vy' : ''));
      const requiredPolicies = ['extra_credit_notice'];
      if (!this.growthbookDynamicValues.funnel_ecbsv_modal) {
        if (this.growthbookDynamicValues.funnel_ecbsv_enabled_public) {
          requiredPolicies.push('ecbsv_authorization');
        } else if (this.growthbookDynamicValues.funnel_ecbsv_enabled_staff && this.form.email.includes('@extra.app')) {
          requiredPolicies.push('ecbsv_authorization');
        }
      } else if (((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.promptECBSV) && this.growthbookDynamicValues.funnel_ecbsv_modal && this.growthbookDynamicValues.funnel_use_finish_controller) {
        requiredPolicies.push('ecbsv_authorization');
      }
      return !_.some(iterables, _.isEmpty) && requiredPolicies.every(key => this.formPolicies.includes(key)) && (!this.formPaymentOptionEnabled || this.formPaymentOptionAgreed);
    },
    personaSubmitted() {
      var _a, _b;
      return ((_b = (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.persona) === null || _b === void 0 ? void 0 : _b.inquiry_status) && !['created', 'pending', 'expired'].includes(this.memberProfile.persona.inquiry_status);
    },
    personaApproved() {
      var _a, _b;
      return ((_b = (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.persona) === null || _b === void 0 ? void 0 : _b.inquiry_status) === 'approved';
    },
    personaFailed() {
      var _a, _b;
      return ((_b = (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.persona) === null || _b === void 0 ? void 0 : _b.inquiry_status) === 'failed';
    },
    personaDeclined() {
      var _a, _b;
      return ((_b = (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.persona) === null || _b === void 0 ? void 0 : _b.inquiry_status) === 'declined';
    },
    decisionReady() {
      return !!(this.decisionStatus && ['approved', 'rejected', 'error'].includes(this.decisionStatus));
    },
    decisionFinal() {
      return ['approved', 'rejected', 'error'].includes(this.decisionStatus);
    },
    decisionFinalRejected() {
      return this.decisionFinal && ['rejected'].includes(this.decisionStatus) && this.kycSubmitted;
      // return false
    },
    readyToSubmit() {
      return !this.error && this.formSubmitted && this.formFilled && this.planSubmitted && this.plaidRemoteReady && !this.kycSubmitted
      // !this.plaidRemoteReady &&
      // && this.memberProfile &&
      // !this.memberProfile.kyc_submitted
;
    },
    accountLastFour() {
      return this.memberProfile && this.memberProfile.bank_account && this.memberProfile.bank_account.mask || null;
    },
    encodedPlan() {
      return this.plan && PLAN_ENCODE_MAP[1][this.plan.selectedPlan];
    },
    encodedCycle() {
      return this.plan && PLAN_ENCODE_MAP[0][this.plan.billingCycle];
    },
    showLoadingOverlay() {
      return this.stageLoading || this.processing || this.submitPlanProcessing || this.redirectTo || this.redirecting;
    },
    processingReason() {
      return this.processingReasonManual || (this.waitingForDecision ? "We are reviewing your information which may take a minute. Please don't close this window!" : this.finishing ? 'Just a moment we’re verifying your information and processing your application.' : null);
    },
    waitingForDecision() {
      return !this.error && (this.plaidReady || this.plaidRemoteReady) && !this.decisionReady
      // && this.finishing
      // && this.readyToSubmit
;
    },
    // showResultScreen() {
    // 	return (
    // 		(!this.kycStatus && this.decisionReady && !this.seenResultScreen) || this.waitingForDecision
    // 	)
    // },
    formattedBirthday() {
      return formatBirthday(this.form.date_of_birth);
    },
    /** Full plan object for `this.plan` */
    selectedPlan() {
      return this.plan.selectedPlan > -1 && this.plan.billingCycle > -1 && this.plans[this.plan.selectedPlan] || null;
    },
    upfrontPrice() {
      if (!this.selectedPlan) {
        return 0;
      }
      return this.selectedPlan.price[this.plan.billingCycle];
    },
    purchaseClass() {
      return this.purchase === true ? 'purchase' : this.purchase === false ? 'not-purchase' : null;
    },
    achPolicyMetadata() {
      var _a, _b;
      // changes here should also be made in agreements.vue, and anything that needs to be saved to the table (isn't display only) should be added to policies.js
      const rawProcessDate = this.alreadyHasSubscription && ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.next_charge) ? dayjs(this.memberProfile.next_charge) // CAU
      : dayjs();
      dayjs.extend(AdvancedFormat); // needed for the 'Do' format
      let currentPlanCost = this.selectedPlan && this.plan && this.selectedPlan.price[this.plan.billingCycle];
      // for already enrolled members (CAU), use their enrolled plan price
      if ((_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.current_plan_name) {
        const currentPlan = getPlanByName(this.memberProfile.current_plan_name);
        if (currentPlan) currentPlanCost = currentPlan.price[this.plan.billingCycle];
      }
      return {
        first_name: this.form.first_name,
        // name at timestamp
        last_name: this.form.last_name,
        // name at timestamp
        account_last_four: this.accountLastFour,
        plan_type: this.encodedPlan,
        billing_cycle: this.encodedCycle,
        basic_plan_cost: this.plans[0].price[this.plan.billingCycle],
        rewards_plan_cost: this.plans[1].price[this.plan.billingCycle],
        // display only
        current_date: dayjs().format('MMMM D, YYYY'),
        process_day: rawProcessDate.format('Do'),
        process_month: this.encodedCycle === 'annual' ? `${rawProcessDate.format('MMMM')} each year` : 'each month',
        raw_process_date: rawProcessDate,
        formatted_process_date: rawProcessDate.format('MMMM Do, YYYY'),
        formatted_plan_cost: currentPlanCost && currentPlanCost.toFixed(2)
      };
    },
    confirmationPolicyMetadata() {
      return {
        recurring_ach_authorization: this.achPolicyMetadata,
        standing_ach_authorization: this.achPolicyMetadata,
        one_time_ach_authorization: this.achPolicyMetadata,
        cardholder_agreement: {
          first_name: this.form.first_name,
          // name at timestamp
          last_name: this.form.last_name // name at timestamp
        },
        patriot_cardholder_agreement: {
          first_name: this.form.first_name,
          // name at timestamp
          last_name: this.form.last_name // name at timestamp
        }
      };
    },
    metadataForCurrentPolicy() {
      return this.confirmationPolicyMetadata[policies[this.disclosureFile]];
    },
    alreadyHasSubscription() {
      var _a;
      return Boolean((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.already_has_subscription);
    },
    policyUpdateRequired() {
      return this.memberProfile && this.memberProfile.policy_update_required;
    },
    alternateFunnel() {
      var _a;
      return (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.alternate_funnel;
    },
    usePatriot() {
      var _a;
      return ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.issuing_bank) === 'patriot';
    },
    isInLeadLOCEligibleState() {
      var _a, _b;
      return this.leadLOCEligibleStates.includes((_b = (_a = this.form) === null || _a === void 0 ? void 0 : _a.address) === null || _b === void 0 ? void 0 : _b.state);
    }
  },
  watch: {
    'form.address.state': {
      deep: true,
      handler(val) {
        if (val) {
          this.checkFormErrors('address_city');
          this.checkFormErrors('address_state');
        }
      }
    },
    planAutopay() {
      // reset checkboxes when switching autopay state
      this.formPolicyConsentTimestamps = {};
    },
    readyToSubmit() {
      // console.log('readytosubmit', 'now:', val, 'prev:', prev)
      this.tryFinish();
    },
    token(val) {
      if (val) {
        this.$cookies.set('members_token', val);
        window.sessionStorage.setItem('EXTRA_MEMBER_TOKEN', val);
        this.$axios.setHeader('Authorization', `Bearer ${val}`);
        this.fetchProfile();
      } else {
        this.logOut(true);
      }
    },
    memberProfile(res, prev) {
      // console.log('memberProfile', res)
      var _a, _b, _c, _d;
      // first load
      if (res && !prev) {
        this.fetchProfile();
        this.$axios.$post('/api/members/user-tag', {
          user_tag: window.g_user_tag || window.g_device_tag
        });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '106_login_loaded',
          email: res.form && res.form.email,
          phone: res.form && res.form.phone,
          member_id: `member_${res.id}`,
          mid: `member_${res.id}`
        });
        // MQL balance check - only check this on first load or it breaks plan changes
        if (res.balance_check) {
          if (res.balance_check.insufficient) {
            this.plaidInsufficient = res.balance_check;
          }
        }
      }
      Object.assign(this.form, res.form);
      this.plan = res.plan || DEFAULT_PLAN;
      this.planSubmitted = this.planSubmitted || !!res.plan;
      this.planAutopay = res.plan_autopay;
      this.allowAutopayChange = res.allow_autopay_change;
      this.billingConfirmed = this.planSubmitted;
      if (this.planSubmitted && !this.plaidRemoteReady) {
        // only fetch the value when it's needed
        this.plaidSectionLayout = this.$growthbook.getFeatureValue('plaid-screen-refresh-nov-2023', 0, {
          memberId: String((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id),
          email: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.form.email
        });
      }
      const plaidOauthURI = window.localStorage.getItem('plaid_oauth_uri');
      if (plaidOauthURI) {
        if (!this.planSubmitted && this.planSelected) {
          this.planSubmitted = true;
          this.billingConfirmed = true;
        }
        window.localStorage.removeItem('plaid_oauth_uri');
        this.openPlaid({
          oauthURI: plaidOauthURI
        });
      }
      // this.plaidAccountId = res.plaid_account_id
      // this.plaidAccessToken = res.plaid_access_token
      this.kycStatus = res.kyc;
      if (res.document_portal) {
        this.kycComplete = res.document_portal.kyc_complete && (!res.document_portal.kyc_required_docs || res.document_portal.kyc_required_docs.length === 0);
        this.agreementsComplete = res.document_portal.agreements_complete;
      }
      this.lock = res.lock;
      if (this.lock && this.lock.body) {
        this.lock.body = this.lock.body.replaceAll('\n\n', '<br/>').replaceAll('\n', '<br/>');
      }
      this.formSubmitted = !!res.kyc_submitted;
      this.kycSubmitted = !!res.kyc_submitted;
      this.ecbsvStatus = ((_c = res === null || res === void 0 ? void 0 : res.ecbsv) === null || _c === void 0 ? void 0 : _c.status) || (res === null || res === void 0 ? void 0 : res.ecbsv) || null;
      this.ecbsvRemainder = ((_d = res === null || res === void 0 ? void 0 : res.ecbsv) === null || _d === void 0 ? void 0 : _d.remainder) || null;
      this.isDev = this.isDev || res.enable_dev_menu || false;
      this.plaidEnv = res.is_demo ? 'sandbox' : this.plaidEnv;
      this.isDemo = res.is_demo || false;
      if (res.user_sfc) {
        this.hashCollision = true;
        this.collisionAttempts = ~~res.user_sfc;
      }
      this.d0Answered = true; // this.d0Answered || res.d0_answered
      this.creditGoalsSurveyAnswered = res.credit_goals_pending_questions.length === 0;
      this.creditBandAnswered = res.credit_band_answered;
      this.code_redeemed = this.code_redeemed || res.code_redeemed;
      this.backupPaymentOnFile = res.backup_payment_on_file || false;
      this.backupPaymentPrompt = res.backup_payment_prompt || false;
      this.handleDecisionStatus({
        status: res.decision,
        ...res
      });
      if (res.shopify_completed && this.form.address) {
        console.log('before', this.form.address);
        this.form.address = Object.assign(DEFAULT_ADDRESS, this.form.address);
        console.log('after', this.form.address);
      } else if (!this.formSubmitted) {
        this.form.address = DEFAULT_ADDRESS;
        // this.form.first_name = null
        // this.form.last_name = null
      }
      // autofill from affiliate PII
      let affPIICookie;
      let affPII;
      try {
        affPIICookie = this.$cookies.get('extra_aff_pii');
        affPII = JSON.parse(atob(decodeURIComponent(affPIICookie || '')) || '{}');
        if (affPII) {
          this.$set(this.form, 'first_name', this.form.first_name || affPII.firstname || affPII.first_name);
          this.$set(this.form, 'last_name', this.form.last_name || affPII.lastname || affPII.last_name);
          this.$set(this.form, 'address', {
            ...this.form.address,
            street_one: this.form.address.street_one || affPII.address,
            street_two: this.form.address.street_two,
            city: this.form.address.city || affPII.city,
            state: this.form.address.state || affPII.state,
            zip_code: this.form.address.zip_code || affPII.zip
          });
        }
      } catch (err) {
        console.warn('aff_pii:', err, affPII, affPIICookie);
      }
      this.$cookies.set('member_id', `member_${res.id}`);
      this.formPaymentOptionEnabled = this.$growthbook.isFeatureOn('14_day_payment_funnel', {
        memberId: String(res.id),
        email: res.form.email
      });
      this.creditBandVariant = this.$growthbook.getFeatureValue('cs5-phase-2', 0, {
        memberId: String(res.id)
      });
      this.updateIdentity();
    },
    plaidInsufficient(val) {
      var _a, _b, _c, _d;
      // writing to this.warningContext shows the insufficient vue
      // plan change modal has its own insufficient screen
      if ((val === null || val === void 0 ? void 0 : val.insufficient) && !this.formShowPlanChangeModal) {
        this.$analyticsHub.track('Plaid Balance Bypass', {
          plan_type: (_a = this.selectedPlan) === null || _a === void 0 ? void 0 : _a.title,
          plan_price: (_b = this.selectedPlan) === null || _b === void 0 ? void 0 : _b.price[this.plan.billingCycle],
          plan_frequency: this.plan.billingCycle === 0 ? 'Monthly' : 'Annual',
          balance: val.balance,
          insufficient: val.insufficient,
          isFeatureOn: val.bypassPlaidBalanceCheck
        });
        if (!val.bypassPlaidBalanceCheck) {
          this.warningContext = {
            context: 'PLAID_INSUFFICIENT',
            details: {
              plan_type: (_c = this.selectedPlan) === null || _c === void 0 ? void 0 : _c.title,
              plan_price: (_d = this.selectedPlan) === null || _d === void 0 ? void 0 : _d.price[this.plan.billingCycle],
              plan_frequency: this.plan.billingCycle === 0 ? 'Monthly' : 'Annual',
              balance: val === null || val === void 0 ? void 0 : val.balance
            }
          };
        }
      }
    },
    kycStatus(val) {
      // console.log('kycStatus watch', !!val)
      if (val) {
        // this.initApto()
      }
    },
    // plaidReady(val) {
    // 	val && this.startPolling()
    // },
    // plaidRemoteReady(val) {
    // 	val && this.startPolling()
    // },
    decisionReady(val) {
      if (val) {
        this.stopPolling();
      }
      this.tryFinish();
      // else {
      // 	this.startPolling()
      // }
    },
    currentStage(val) {
      var _a, _b, _c, _d, _e, _f, _g;
      setTimeout(() => {
        this.$scrollTo('header', 1, {
          duration: 1,
          offset: 0,
          force: true,
          cancelable: true
        });
      }, 350);
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'stage',
        phylum: val
      }]);
      this.$axios.setHeader('X-Extra-Funnel-Stage', val);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'funnel-position',
        stage: val
      });
      this.$analyticsHub.page('funnel-position', val);
      window.history.replaceState({}, '', '/members/card/' + val + window.location.search);
      this.stageLoading = false;
      this.formShowPlanChangeModal = false;
      if (!val) {
        this.errorContext = {
          context: 'computed.currentStage'
        };
        this.error = new Error('Missing state ' + val);
        console.log(this.errorContext, this.error);
      } else if (val === STAGE.form) {
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: 'kyc_start'
        }]);
        this.$analyticsHub.track('EAS Form Viewed', {
          member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id,
          plan_type: (_b = this.selectedPlan) === null || _b === void 0 ? void 0 : _b.title,
          plan_price: (_c = this.selectedPlan) === null || _c === void 0 ? void 0 : _c.price[this.plan.billingCycle],
          plan_frequency: this.plan.billingCycle === 0 ? 'Monthly' : 'Annual',
          external_id: `member_${(_d = this.memberProfile) === null || _d === void 0 ? void 0 : _d.id}`,
          // segment will hash
          eventID: `InitiateCheckout.${(_e = this.memberProfile) === null || _e === void 0 ? void 0 : _e.id}`,
          event_time: Math.round(Date.now() / 1000)
        });
        const eventID = uuidV4();
        const eventName = 'InitiateCheckout';
        const eventParams = {
          contents: [{
            id: (_f = this.selectedPlan) === null || _f === void 0 ? void 0 : _f.title,
            quantity: 1,
            item_price: (_g = this.selectedPlan) === null || _g === void 0 ? void 0 : _g.price[this.plan.billingCycle]
          }],
          content_ids: [this.plan.billingCycle === 0 ? 'Monthly' : 'Annual'],
          ...(this.plan && {
            currency: 'USD',
            value: this.upfrontPrice
          })
        };
        const fbArgs = [eventName, {
          ...eventParams
        }, {
          eventID
        }];
        this.$fb.track(...fbArgs);
        this.$axios.$post('/api/fbevent', {
          // CAPI
          eventName,
          member: {
            ...this.memberProfile,
            external_id: this.$cookies.get('ajs_anonymous_id')
          },
          eventId: eventID,
          eventParams
        }).catch(err => {
          var _a;
          console.log(`[fbEvent] ${eventName} CAPI failed for mid: ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id} with error: ${err}`);
        });
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '180_kyc_start'
        });
        // } else if (val === STAGE.approved) {
        // 	// this is a deprecated screen
        // 	window.extra_data_ul.log([
        // 		{
        // 			app: 'sales-funnel',
        // 			kingdom: 'approval_screen',
        // 		},
        // 	])
      } else if (val === STAGE.confirmed || val === STAGE.pendingKYC) {
        if (val === STAGE.confirmed) {
          // confirmation screen
          /* moved to /finish resolve */
          // window.dataLayer = window.dataLayer || []
          // window.dataLayer.push({ event: '189_kyc_success' })
        }
      } else if (val === STAGE.plan) {
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: 'plan_select_start'
        }]);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '120_plan_select_start'
        });
        // } else if (val === STAGE.billing) {
        // 	// this is a deprecated screen
        // 	window.extra_data_ul.log([
        // 		{
        // 			app: 'sales-funnel',
        // 			kingdom: 'billing_confirm_start',
        // 		},
        // 	])
        // 	window.dataLayer = window.dataLayer || []
        // 	window.dataLayer.push({ event: '140_billing_confirm_start' })
      } else if (val === STAGE.questions) {
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: 'questionnaire_start'
        }]);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '110_questionnaire_start'
        });
      }
    },
    error(err) {
      // console.log('errrr', err)
      if (!err) {
        this.errorContext = null;
        return;
      }
      this.stopPolling();
      this.formSubmitted = false;
      const isErr = err instanceof Error || err && err.stack && err.message;
      // console.log('iserr', isErr)
      const casted = isErr ? err : new Error(JSON.stringify(err));
      if (this.errorContext) {
        Object.entries(this.errorContext).forEach(([key, value]) => {
          casted[key] = value;
        });
      }
      const id = this.$sentry.captureException(casted);
      console.error(err.response, this.errorContext, id);
      try {
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: 'error',
          phylum: casted.name || err.name,
          class: casted.name || err.message,
          context: casted.context,
          sentry: id,
          json: JSON.stringify(err),
          responseData: err.response && JSON.stringify(err.response.data),
          response: err.response && JSON.stringify(err.response)
        }]);
      } catch (_a) {}
      console.error(`MID ${this.memberProfile && this.memberProfile.id}`, err, JSON.stringify(err)); // errorContext is included in err
    },
    ecbsvInvalid(val) {
      if (val && this.form && this.formErrors) {
        // this.form.ssn = ''
        // this.form.date_of_birth = ''
        this.formErrors.ssn = true;
        this.formErrors.date_of_birth = true;
        this.formSubmitted = false;
        setTimeout(() => {
          this.$scrollTo('#ecbsv-scroll');
        }, 100);
      }
    },
    redirectTo(val) {
      if (val) {
        this.$router.push(val);
      }
    },
    isInLeadLOCEligibleState() {
      let tempPolicySet = [];
      if (this.formPolicies.includes('ecbsv_authorization')) {
        tempPolicySet = this.formPolicies.filter(policy => policy === 'ecbsv_authorization');
      }
      this.formPolicies = tempPolicySet; // reset checkbox
    }
  },
  mounted() {
    console.log(this.$route.params);
    // fetch member from api
    // on throw, set member token to null, log out
    this.$cookies.remove('members_phone');
    this.fromApp = !!(window === null || window === void 0 ? void 0 : window.sessionStorage.getItem('EXTRA_FROM_APP'));
    this.token = this.$cookies.get('members_token') || window.sessionStorage.getItem('EXTRA_MEMBER_TOKEN');
    if (!this.token) this.logOut(true);
    if (window.EXTRA_DEV) {
      this.isDev = true;
    }
    if (this.isDev) {
      window._manualPlaid = (publicToken, accountId) => {
        this.onPlaidSuccess(publicToken, {
          account_id: accountId
        });
      };
      window.fillForm = () => this.form = FORM_DEBUG;
      window._getFingerprints = () => {
        this.getFingerprints().then(console.log).catch(console.error);
      };
      if (this.$config.debugForm) {
        DEFAULT_FORM.date_of_birth = '01/01/2000';
        DEFAULT_FORM.ssn = '111-11-1111';
      }
      this.forcedFraudResult = 'approved';
      // Object.keys(this.formPolicies).forEach((k) => (this.formPolicies[k] = true))
    }
    this.leadLOCEligibleStates = this.$growthbook.getFeatureValue('lead_eligible_states', []);
    this.mlaNoticeEnabled = this.$growthbook.isFeatureOn('mla_notice_funnel');
    const ecbsv_enabled_staff = this.$growthbook.getFeatureValue('funnel_ecbsv_enabled_staff', false);
    const ecbsv_enabled_public = this.$growthbook.getFeatureValue('funnel_ecbsv_enabled_public', false);
    const funnel_ecbsv_modal = this.$growthbook.getFeatureValue('funnel_ecbsv_modal', false);
    const funnel_use_finish_controller = this.$growthbook.getFeatureValue('funnel_use_finish_controller', false);
    this.growthbookDynamicValues = {
      funnel_ecbsv_enabled_staff: ecbsv_enabled_staff,
      funnel_ecbsv_enabled_public: ecbsv_enabled_public,
      funnel_ecbsv_modal,
      funnel_use_finish_controller
    };
    this.surveyFilled = true; // turning survey perma off for now
  },
  beforeDestroy() {
    clearTimeout(this.decisionPollTimeout);
    this.decisionPollTimeout = undefined;
    clearTimeout(this.kycStatusPollTimeout);
    this.kycStatusPollTimeout = undefined;
  },
  errorCaptured(err, vm, info) {
    err.info = info;
    this.error = err;
    this.errorContext = {
      context: 'uncapturedExc'
    };
    return true;
  },
  methods: {
    viewedDisclosure() {
      this.showDisclosure = false;
    },
    showThisDisclosure(disc) {
      this.disclosureFile = disc;
      this.showDisclosure = true;
    },
    logOut(replace = false) {
      this.$cookies.remove('members_token');
      window.sessionStorage.removeItem('EXTRA_MEMBER_TOKEN');
      this.$router[replace ? 'replace' : 'push']('/members/login');
    },
    checkFormErrors(field) {
      const e = getFormErrors(this.form, this.formattedBirthday);
      if (field) {
        this.$set(this.formErrors, field, e[field]);
        // console.log(source, field, 'err=', e[field])
      } else {
        this.formErrors = {
          ...e
        };
      }
      return {
        ...e
      };
    },
    clearForm(obj) {
      const target = obj || this.form;
      Object.entries(target).forEach(([k, v]) => {
        if (typeof v === 'object') {
          this.clearForm(this.form[k]);
        } else {
          target[k] = '';
        }
      });
      this.$set(this, 'formErrors', {});
      Object.keys(this.formPolicies).forEach(k => {
        this.formPolicies[k] = false;
      });
    },
    updateIdentity() {
      var _a, _b, _c, _d, _e, _f;
      const id = (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id;
      const mid = id && `member_${id}`;
      const partnerId = (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.partnerId;
      const name = [this.form.first_name || ((_c = this.memberProfile) === null || _c === void 0 ? void 0 : _c.form.first_name), this.form.last_name || ((_d = this.memberProfile) === null || _d === void 0 ? void 0 : _d.form.last_name)].filter(e => e).join(' ') || undefined;
      const email = this.form.email || ((_e = this.memberProfile) === null || _e === void 0 ? void 0 : _e.form.email);
      const phone = this.form.phone || ((_f = this.memberProfile) === null || _f === void 0 ? void 0 : _f.form.phone);
      this.$sentry.setUser({
        email,
        id: mid
      });
      this.$ddLogs.addLoggerGlobalContext('user.mid', mid);
      this.$ddLogs.addLoggerGlobalContext('user.name', name);
      this.$ddLogs.addLoggerGlobalContext('user.email', email);
      this.$ddLogs.addLoggerGlobalContext('user.phone', phone);
      this.$ddLogs.addLoggerGlobalContext('user.is_patriot', this.usePatriot);
      this.$ddLogs.addLoggerGlobalContext('user.partnerId', partnerId);
      // Set RUM Session info for user
      this.$ddRum.setUser({
        id: String(id),
        mid: id,
        name,
        email,
        is_patriot: this.usePatriot,
        partnerId
      });
      const phoneWithArea = (phone || '').startsWith('+') ? phone : '+1' + phone;
      const phoneDigitsWithArea = (phoneWithArea || '').replace(/\+/, '') || undefined;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'identity_update',
        email,
        phone,
        mid,
        member_id: mid,
        name,
        sha256_email: email && sha256(email),
        sha256_phone: phone && sha256(phone),
        phone_with_area: phoneWithArea,
        sha256_phone_with_area: phoneWithArea && sha256(phoneWithArea),
        phone_digits_with_area: phoneDigitsWithArea,
        sha256_phone_digits_with_area: phoneDigitsWithArea && sha256(phoneDigitsWithArea)
      });
    },
    onFieldFocus(name) {
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'kyc_input',
        phylum: name
      }]);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: '185_kyc_input',
        field: name
      });
    },
    onFieldBlur(name) {
      this.checkFormErrors(name);
    },
    onFieldChange(name, anotherFieldToCheck) {
      var _a;
      this.checkFormErrors(name);
      if (anotherFieldToCheck) {
        this.checkFormErrors(anotherFieldToCheck);
      }
      if (!this.formStarted) {
        this.$analyticsHub.track('EAS Form Started', {
          member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id
        });
        this.formStarted = true;
      }
    },
    onTestflightClick() {
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'engagement',
        phylum: 'button_press',
        class: 'testflight'
      }]);
    },
    onSeenResultScreen() {
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'engagement',
        phylum: 'button_press',
        class: 'approval_continue'
      }]);
      this.seenResultScreen = true;
    },
    onQuestionnaireSubmit(answersRemapped) {
      this.d0Answered = true;
      this.$axios.$post('/api/members/questionnaire', answersRemapped).then(response => console.log('questionnaire submitted', response)).catch(err => {
        alert('Something went wrong while submitting your answers. Please try again!');
        this.d0Answered = false;
        console.error('questionnaire err', err);
        this.$sentry.captureException(err);
      });
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'questionnaire_complete'
      }]);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: '119_questionnaire_complete'
      });
    },
    async fetchProfile() {
      var _a, _b, _c, _d, _e, _f;
      try {
        this.processing = true;
        const memberData = await this.$axios.$get('/api/members/profile' + (this.fromApp ? '?from_app=1' : ''), {
          headers: {
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache',
            Expires: '0'
          }
        });
        this.memberProfile = memberData;
        if ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.bank_account) this.memberProfile.bank_linked = true;
        this.ecbsvHardRejection = (((_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.ecbsv) || ((_d = (_c = this.memberProfile) === null || _c === void 0 ? void 0 : _c.ecbsv) === null || _d === void 0 ? void 0 : _d.status)) === 'rejected';
        this.promptECBSV = (_e = this.memberProfile) === null || _e === void 0 ? void 0 : _e.promptECBSV;
        console.log('fetchProfile alternateFunnel', this.alternateFunnel);
        if (this.alternateFunnel === 'shopify') {
          this.$cookies.set('shopify_prefill_cache', JSON.stringify(this.memberProfile.form), {
            domain: '.extra.app'
          });
          console.log('shopify_prefill_cache', this.memberProfile.form);
          this.redirecting = true;
          setTimeout(() => {
            window.location.href = 'https://shop.extra.app/products/extra-card';
          }, 650);
          return;
        }
        // Don't remove - last step
        this.processing = false;
      } catch (err) {
        this.processing = false;
        this.errorContext = {
          context: 'fetchProfile'
        };
        this.error = err;
        if ((_f = err.response) === null || _f === void 0 ? void 0 : _f.data) {
          if (JSON.stringify(err.response.data).includes('queue-inline-autoconvert-error workaround')) {
            this.errorContext = {
              context: 'queue-inline-autoconvert-error workaround'
            };
          }
        }
        console.error(err);
      }
    },
    async onPolicyUpdateComplete() {
      var _a;
      if ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.billing_confirmed) {
        await this.$axios.$get('/api/members/update-signup-cache-agreement');
      }
      this.fetchProfile();
    },
    clearPlaidLocal() {
      this.plaidInsufficient = this.planSubmitted ? null : this.plaidInsufficient; // accounts for user navigating to 'change plan' stage from the 'insufficient funds' stage
      this.plaidProcessing = false;
      this.plaidPublicToken = null;
      this.plaidAccessToken = null;
      this.plaidAccountId = null;
      this.plaidMetadata = null;
      this.plaidAccountType = null;
      this.corporateAccountInfo = null;
      this.warningContext = null;
    },
    insufficientPlaidRetry() {
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'plaid_insufficient_retry_btn'
      }]);
      this.resetPlaid();
      // this is required in case plaid link is removed
      this.fetchProfile().then(() => console.log('profile fetched during insufficient plaid retry'));
      this.clearPlaidLocal();
    },
    insufficientBillingBack() {
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'plaid_insufficient_billing_back_button'
      }]);
      this.planSubmitted = false;
      this.clearPlaidLocal();
    },
    async formPlanChangeInsufficientCheck(upfrontPlanPrice) {
      var _a, _b;
      if (typeof upfrontPlanPrice !== 'number') throw new Error('Price must be provided');
      try {
        this.plaidInsufficient = await this.$axios.$post('/api/members/plaid/balance-check', {
          upfrontPlanPrice,
          fetchLatest: true
        });
      } catch (err) {
        console.error(`formPlanChangeInsufficientCheck error for MID ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id}`, err);
        this.formShowPlanChangeModal = false;
        if (((_b = err.response) === null || _b === void 0 ? void 0 : _b.status) === 403) {
          await this.$nextTick();
          this.showPlaidRelinkErrorScreen(err);
        } else {
          throw err;
        }
      }
    },
    setPaymentOption(paymentOption) {
      this.paymentOption = paymentOption;
      this.formPaymentOptionAgreed = true;
      if (this.formFilled) {
        // trigger EAS form submission
        this.addressAuthVerify();
      }
    },
    resetToNextDay() {
      this.paymentOption = PaymentOption.NEXT_DAY_ACH;
      this.formPaymentOptionAgreed = false;
    },
    softRetryPlaid() {
      // Object.assign(this.memberProfile, { bank_linked: false })
      // this.submitPlaid()
      // this.plaidAccountId = null
      // this.plaidAccessToken = null
      // this.plaidPublicToken = null
      // this.decisionStatus = null
      // this.plaidProcessing = false
      // this.plaidMetadata = null
      // this.error = null
      // this.errorContext = null
      this.openPlaid();
      // window.location.reload()
    },
    plaidFillFail() {
      this.onPlaidSuccess('public-sandbox-internal-test', {
        institution: {
          name: 'Chase',
          institution_id: 'ins_3'
        },
        account: {
          id: 'internal-test',
          name: 'internal-test',
          type: 'depository',
          subtype: 'checking',
          mask: '0000'
        },
        account_id: 'internal-test',
        accounts: [{
          id: 'internal-test',
          name: 'internal-test',
          mask: '0000',
          type: 'depository',
          subtype: 'checking'
        }],
        link_session_id: 'internal-test',
        public_token: 'public-sandbox-internal-test'
      });
    },
    plaidFillCache() {
      const {
        publicToken,
        metadata
      } = JSON.parse(window.localStorage.getItem('last_plaid_cache') || '{}');
      this.onPlaidSuccess(publicToken, metadata);
    },
    fetchPlaidLinkToken(payload) {
      var _a;
      return this.$axios.$post('/api/members/plaid/link-token', {
        plan: this.planSelected ? this.plan : null,
        planName: this.planSelected ? (_a = this.selectedPlan) === null || _a === void 0 ? void 0 : _a.name : null,
        ...payload
      });
    },
    async openPlaid(config = {}) {
      var _a, _b, _c;
      try {
        this.plaidProcessing = true;
        if (!window.Plaid) {
          console.log('plaid not initialized, trying in 500ms');
          await sleep(500);
          return this.openPlaid(config);
        }
        const {
          oauthURI
        } = config;
        this.plaidAccessToken = null;
        this.plaidPublicToken = null;
        this.plaidAccountId = null;
        this.plaidMetadata = null;
        const {
          link_token
        } = await this.fetchPlaidLinkToken({
          previous: oauthURI && true
        });
        const plaidLink = window.Plaid.create({
          token: link_token,
          ...(oauthURI && {
            receivedRedirectUri: oauthURI
          }),
          onLoad: this.onPlaidLoad,
          onSuccess: this.onPlaidSuccess,
          onExit: this.onPlaidExit,
          onEvent: this.onPlaidEvent
        });
        this.linkToken = link_token;
        // userLegalName: `${this.form.first_name} ${this.form.last_name}`,
        // userEmailAddress: this.form.email,
        plaidLink.open();
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: this.plaidRefreshToken ? 'plaid_link_refresh_start' : 'plaid_link_start'
        }]);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '160_plaid_link_start'
        });
        this.$analyticsHub.track('Plaid Link Started', {
          member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id,
          external_id: `member_${(_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.id}`,
          // segment will hash
          eventID: `AddPaymentInfo.${(_c = this.memberProfile) === null || _c === void 0 ? void 0 : _c.id}`,
          event_time: Math.round(Date.now() / 1000)
        });
        const eventID = uuidV4();
        const eventParams = {
          custom_data: {
            currency: 'USD'
          }
        };
        const fbArgs = ['AddPaymentInfo', {
          ...eventParams
        }, {
          eventID
        }];
        this.$fb.track(...fbArgs);
        this.$axios.$post('/api/fbevent', {
          // CAPI
          eventName: 'AddPaymentInfo',
          member: {
            ...this.memberProfile,
            external_id: this.$cookies.get('ajs_anonymous_id')
          },
          eventParams,
          eventId: eventID
        }).catch(err => {
          var _a;
          console.log(`[fbEvent] AddPaymentInfo CAPI failed for mid: ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id} with error: ${err}`);
        });
        this.$axios.$post('/api/efevent', {
          eventId: EverflowEvents.PLAID_LINK_STARTED
        });
        // if (this.plaidRefreshToken) {
        // force
        // this.decisionStatus = null
        // this.pollStatus(true)
        // }
      } catch (err) {
        this.plaidProcessing = false;
        alert('Something went wrong :( Our team was notified of the issue. Please try again, or reach out to Member Concierge, so we can solve this right away!');
        this.$sentry.captureException(err);
        throw err;
      }
    },
    onPlaidLoad() {
      // console.log('handlePlaidOnLoad')
    },
    onPlaidSuccess(publicToken, metadata) {
      var _a;
      if (this.isDev) {
        window.localStorage.setItem('last_plaid_cache', JSON.stringify({
          publicToken,
          metadata
        }));
      }
      // clear error
      this.error = null;
      this.errorContext = null;
      this.plaidProcessing = false;
      this.plaidPublicToken = publicToken;
      this.plaidAccessToken = null;
      this.plaidAccountId = metadata.account_id;
      this.plaidMetadata = metadata;
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: this.plaidRefreshToken ? 'plaid_link_refresh_complete' : 'plaid_link_complete',
        phylum: 'success'
      }]);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: '169_plaid_linked'
      });
      this.$analyticsHub.track('Plaid Link Confirmed', {
        member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id
        // plaid_token: this.linkToken, - do not track for security reasons
      });
      // console.log('[plaid] onPlaidSuccess for MID', this.memberProfile.id)
      this.$axios.$post('/api/efevent', {
        eventId: EverflowEvents.PLAID_LINK_CONFIRMED
      });
      if (this.plaidRefreshToken) {
        this.onPlaidRefreshFinish();
      } else {
        this.submitPlaid();
      }
    },
    onPlaidExit(err, metadata) {
      var _a;
      this.plaidProcessing = false;
      this.plaidMetadata = metadata;
      const sessionId = metadata && metadata.link_session_id;
      if (err) {
        this.plaidProcessing = false;
        this.plaidAccessToken = null;
        this.plaidPublicToken = null;
        this.plaidAccountId = null;
        // kick to /error
        this.errorContext = {
          context: err.error_code,
          details: {
            link_session_id: sessionId
          }
        };
        console.log('onPlaidExit error for MID', (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id, err.error_code);
        this.error = new Error(err.error_code);
        this.error.json = JSON.stringify({
          ...err,
          ...metadata
        });
      }
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'plaid_exit',
        phylum: metadata.status,
        class: err && err.error_code,
        subclass: err && err.error_type,
        family: metadata.institution && metadata.institution.institution_id,
        group_tag: sessionId
      }]);
      if (this.plaidRefreshToken) {
        this.onPlaidRefreshFinish();
      }
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: this.plaidRefreshToken ? 'plaid_link_refresh_complete' : 'plaid_link_complete',
        phylum: 'fail',
        class: err ? 'error' : null
      }]);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: '168_plaid_link_error'
      });
    },
    onPlaidEvent(eventName, metadata) {
      var _a;
      const {
        timestamp: plaid_timestamp,
        ...plaidMetadata
      } = metadata;
      this.$analyticsHub.track('Plaid Link Event', {
        member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id,
        event_name: eventName,
        plaid_timestamp,
        ..._.pickBy(plaidMetadata, Boolean)
      });
    },
    async onPlaidRefreshFinish() {
      this.decisionStatus = null;
      this.processing = true;
      this.processingReasonManual = "Hold on, we're connecting to your bank...";
      // this.plaidRefreshToken = null
      this.error = null;
      this.errorContext = null;
      await this.fetchStatus(true);
      this.startPolling(3500);
      this.processing = false;
      this.processingReasonManual = null;
    },
    softSubmitForm() {
      var _a, _b;
      const exampleDate = this.isDateSupported ? '1998-11-21' : '11/21/1998';
      if (!this.formattedBirthday || dayjs().diff(this.formattedBirthday, 'years') < 18) {
        alert(`Make sure you entered your birthdate in the correct format (e.g ${exampleDate}). You have to be 18 years or older to use Extra`);
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: 'bday_under_18'
        }]);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '186_bday_under_18'
        });
        this.finishing = false;
        return;
      }
      this.checkFormErrors();
      if (!this.formFilled) {
        if (_.isEmpty(this.formErrors)) {
          alert('Make sure you filled all fields correctly');
          this.finishing = false;
          return;
        }
        const list = Object.entries(this.formErrors).map(([key, val]) => {
          return val && {
            first_name: 'first name',
            last_name: 'last name',
            email: 'email',
            phone: 'phone',
            address_street_one: 'street',
            address_zip_code: 'ZIP code',
            address_state: 'state',
            address_city: 'city',
            date_of_birth: 'date of birth',
            ssn: 'SSN'
          }[key];
        }).filter(e => e);
        alert(`Make sure you filled the ${list.length ? `${list.join(', ')} field${list.length > 1 ? 's' : ''} and the ` : ''}checkboxes correctly`);
        this.finishing = false;
        return;
      }
      // should update readyToSubmit and trigger tryFinish
      this.formSubmitted = true;
      this.updateIdentity();
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'kyc_filled_out'
      }]);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: '187_kyc_filled_out'
      });
      // prep segment traits
      const seg_trait_age = dayjs().diff(dayjs(this.form.date_of_birth), 'year');
      const seg_trait_address = {
        street: this.form.address.street_one + ' ' + this.form.address.street_two,
        street_one: this.form.address.street_one,
        street_two: this.form.address.street_two,
        city: this.form.address.city,
        state: this.form.address.state,
        postalCode: this.form.address.zip_code,
        country: 'USA'
      };
      this.$analyticsHub.identify((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id, {
        address: seg_trait_address,
        age: seg_trait_age,
        birthday: this.form.date_of_birth
      });
      this.$analyticsHub.track('EAS Form Submitted', {
        member_id: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.id
      });
    },
    addressAuthVerify(e) {
      e === null || e === void 0 ? void 0 : e.preventDefault();
      if (this.form && this.form.address && this.form.address.street_one && this.form.address.street_one.length && this.form.address.city && this.form.address.city.length && this.form.address.state && this.form.address.state.length && this.form.address.zip_code && this.form.address.zip_code.length) {
        this.finishing = true;
        this.$axios.$post('/api/smarty/verify-address', {
          streetOne: this.form.address.street_one,
          streetTwo: this.form.address.street_two,
          city: this.form.address.city,
          state: this.form.address.state,
          zip: this.form.address.zip_code
        }).then(data => {
          var _a;
          let areCommercial = true;
          let arePOBox = true;
          if (_.isEqual(this.cachedAddresses[this.form.address.street_one], this.form.address)) {
            this.softSubmitForm();
            return;
          }
          const lookupResults = ((_a = data === null || data === void 0 ? void 0 : data.lookup) === null || _a === void 0 ? void 0 : _a.result) || [];
          // there may be multiple candidates per lookup
          lookupResults.forEach(address => {
            if (address.metadata.rdi !== 'Commercial') {
              // At least 1 result is non-Commercial
              areCommercial = false;
            }
            if (address.metadata.recordType !== 'P') {
              // At least 1 result is not a PO Box
              arePOBox = false;
            }
          });
          const validMatches = lookupResults.filter(address => {
            // Filter out Non-residential addresses
            if (address.metadata.rdi !== 'Residential') {
              return false;
            }
            // Filter out PO Box addresses
            if (address.metadata.recordType === 'P') {
              return false;
            }
            // No record type because address did not make a valid Delivery Point Validation (DPV) match
            // Filter out blank recordType address
            if (!address.metadata.recordType) {
              return false;
            }
            return true;
          });
          this.$analyticsHub.track('EAS Form Address Verification', {
            areCommercial,
            arePOBox,
            numResults: lookupResults.length,
            numValidResults: validMatches.length
          });
          if (areCommercial) {
            this.addressErrorMessage = 'We unfortunately cannot accept commercial addresses at this time. Please provide a residential address to proceed.';
            this.finishing = false;
            return;
          }
          if (arePOBox) {
            this.addressErrorMessage = 'We unfortunately cannot accept P.O Boxes at this time. Please provide a residential address to proceed.';
            this.finishing = false;
            return;
          }
          if (!validMatches.length) {
            this.addressErrorMessage = 'We were unable to validate your address at this time. Please try again or contact Extra Concierge for assistance at hey@extra.app.';
            this.finishing = false;
            return;
          }
          const perfectMatch = validMatches.some(address => {
            return address.deliveryLine1 === this.form.address.street_one && address.components.cityName === this.form.address.city && address.components.state === this.form.address.state && address.components.zipCode === this.form.address.zip_code;
          });
          if (!perfectMatch) {
            this.addressSuggestions = validMatches.slice();
            this.finishing = false;
            return;
          }
          this.softSubmitForm();
        }).catch(err => {
          this.softSubmitForm();
          this.$sentry.captureException(err);
        });
      }
    },
    async submitPlan() {
      var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
      // without this, if user clicks Change Plan from the form page and has insufficient funds, the form flashes for a sec before the insufficient page shows (because fetchProfile sets processing=false)
      this.submitPlanProcessing = true;
      if (this.planSelected) {
        this.planSubmitted = true;
      }
      // plaidInsufficient check against current selected plan price
      const currentPlanPrice = this.plans[this.plan.selectedPlan].price[this.plan.billingCycle];
      if (((_a = this.plaidInsufficient) === null || _a === void 0 ? void 0 : _a.insufficient) && currentPlanPrice < this.plaidInsufficient.balance) {
        this.plaidInsufficient = null;
      }
      // browser back button clicks requires re-fetching profile (link might be removed due to nsf etc.)
      // however this also update plan object which effect current selection. Damn too complicated!
      const plan = this.plan;
      await this.fetchProfile();
      this.plan = plan;
      // if already a cardholder
      if (this.alreadyHasSubscription) {
        this.plaidInsufficient = null;
      } else if (this.plaidAccountId || ((_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.bank_linked)) {
        const upfrontPlanPrice = (_c = this.selectedPlan) === null || _c === void 0 ? void 0 : _c.price[this.plan.billingCycle];
        let result;
        try {
          result = await this.$axios.$post('/api/members/plaid/balance-check', {
            plan: this.planSelected ? this.plan : null,
            planName: this.planSelected ? (_d = this.selectedPlan) === null || _d === void 0 ? void 0 : _d.name : null,
            upfrontPlanPrice,
            fetchLatest: true
          });
        } catch (err) {
          console.error(`submitPlan balance check error for MID ${(_e = this.memberProfile) === null || _e === void 0 ? void 0 : _e.id}`, err);
          this.formShowPlanChangeModal = false;
          if (((_f = err.response) === null || _f === void 0 ? void 0 : _f.status) === 403) {
            await this.$nextTick();
            this.showPlaidRelinkErrorScreen(err);
            return;
          } else {
            throw err;
          }
        }
        window.setTimeout(() => this.submitPlanProcessing = false, 500);
        if (result.insufficient) {
          this.plaidInsufficient = result;
          this.processing = false;
          this.processingReasonManual = null;
          if (!result.bypassPlaidBalanceCheck) return;
        }
      }
      this.submitPlanProcessing = false;
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'plan_select',
        phylum: this.encodedPlan,
        class: this.encodedCycle,
        subclass: 'can_pick_annual',
        family: 'threshold_200'
      }]);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: '129_plan_selected',
        plan_type: this.encodedPlan,
        plan_cycle: this.encodedCycle
      });
      this.$analyticsHub.identify((_g = this.memberProfile) === null || _g === void 0 ? void 0 : _g.id, {
        member_id: (_h = this.memberProfile) === null || _h === void 0 ? void 0 : _h.id
      });
      this.$analyticsHub.track('Membership Plan Confirmed', {
        event_datetime: new Date().toISOString(),
        member_id: (_j = this.memberProfile) === null || _j === void 0 ? void 0 : _j.id,
        plan_type: (_k = this.selectedPlan) === null || _k === void 0 ? void 0 : _k.title,
        plan_price: (_l = this.selectedPlan) === null || _l === void 0 ? void 0 : _l.price[this.plan.billingCycle],
        plan_frequency: this.plan.billingCycle === 0 ? 'Monthly' : 'Annual'
      });
      if (!((_m = this.memberProfile) === null || _m === void 0 ? void 0 : _m.plan)) {
        const upfrontPrice = this.plans[this.plan.selectedPlan].price[this.plan.billingCycle];
        let plan = this.plan.billingCycle ? 'a' : 'm';
        plan += this.plan.selectedPlan ? 'r' : '';
        plan += `_${upfrontPrice}`;
        const kvps = [{
          key: 'Adv1',
          val: plan
        }];
        this.$axios.$post('/api/efevent', {
          eventId: EverflowEvents.MEMBERSHIP_PLAN_CONFIRMED,
          additionalVars: kvps
        });
      }
    },
    async confirmPlan(autoSubmitted = false) {
      var _a, _b, _c, _d, _e, _f;
      if (this.processing) return;
      this.processing = true;
      let remappedPolicies = {};
      if (autoSubmitted) {
        remappedPolicies.standing_ach_authorization = this.formPolicyConsentTimestamps.extra_credit_notice;
        remappedPolicies.cardholder_agreement = this.formPolicyConsentTimestamps.extra_credit_notice; // Can remove later
        remappedPolicies[this.planAutopay ? 'recurring_ach_authorization' : 'one_time_ach_authorization'] = this.formPolicyConsentTimestamps.extra_credit_notice;
      } else {
        remappedPolicies = this.confirmationPolicyConsentTimestamps;
      }
      if (this.usePatriot) {
        if (autoSubmitted) {
          remappedPolicies.patriot_cardholder_agreement = this.formPolicyConsentTimestamps.extra_credit_notice;
        } else {
          remappedPolicies.patriot_cardholder_agreement = this.confirmationPolicyConsentTimestamps.cardholder_agreement;
        }
        // Reset/remove evolve cardholder agreement consent
        delete remappedPolicies.cardholder_agreement;
      }
      if ((_a = this.formPolicyConsentTimestamps) === null || _a === void 0 ? void 0 : _a.ecbsv_authorization) {
        remappedPolicies.ecbsv_authorization = this.formPolicyConsentTimestamps.ecbsv_authorization;
      }
      // user has Insufficient Plaid Balance but was allowed to convert
      const ipbBypass = ((_b = this.plaidInsufficient) === null || _b === void 0 ? void 0 : _b.insufficient) && ((_c = this.plaidInsufficient) === null || _c === void 0 ? void 0 : _c.bypassPlaidBalanceCheck);
      // confirm-cardholder api tracks conversion for EF (Application Complete)
      await this.$axios.post('/api/members/confirm-cardholder', {
        plan: this.alreadyHasSubscription ? undefined : this.plan,
        plan_price: this.alreadyHasSubscription ? undefined : (_d = this.selectedPlan) === null || _d === void 0 ? void 0 : _d.price[this.plan.billingCycle],
        planAutopay: this.alreadyHasSubscription ? undefined : this.planAutopay,
        ipbBypass,
        policyTimestamps: remappedPolicies,
        policyMetadata: _.pick(this.confirmationPolicyMetadata, Object.keys(remappedPolicies)),
        alreadyHasSubscription: this.alreadyHasSubscription,
        traceId: this.traceId,
        usePatriot: this.usePatriot
      });
      if (this.alreadyHasSubscription) {
        // existing cardholders should go to the download page
        if (this.fromApp) {
          this.$router.push('/app/dismiss');
        } else {
          this.$router.push('/download');
        }
      } else {
        // update agreements_complete and memberProfile.mla_covered
        await this.fetchProfile();
        window.dataLayer.push({
          event: ipbBypass ? 'ipb_debit_cardholder_created' : 'debit_cardholder_created',
          plan_cycle: this.encodedCycle,
          plan_type: (_e = this.selectedPlan) === null || _e === void 0 ? void 0 : _e.title,
          plan_price: (_f = this.selectedPlan) === null || _f === void 0 ? void 0 : _f.price[this.plan.billingCycle],
          patriot: this.usePatriot
        });
        let eventName;
        const eventID = uuidV4();
        const eventParams = this.selectedPlan ? {
          currency: 'USD',
          value: this.selectedPlan.price[this.plan.billingCycle]
        } : {};
        if (ipbBypass) {
          eventName = 'SubscribePending';
          this.$fb.query('trackCustom', eventName, eventParams, {
            eventID
          });
        } else {
          eventName = 'Subscribe';
          const fbArgs = [eventName, {
            ...eventParams
          }, {
            eventID
          }];
          this.$fb.track(...fbArgs);
        }
        this.$axios.$post('/api/fbevent', {
          // CAPI
          eventName,
          member: {
            ...this.memberProfile,
            external_id: this.$cookies.get('ajs_anonymous_id')
          },
          eventParams: {
            custom_data: eventParams
          },
          eventId: eventID
        }).catch(err => {
          var _a;
          console.log(`[fbEvent] ${eventName} CAPI failed for mid: ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id} with error: ${err}`);
        });
      }
      this.$analyticsHub.track(ipbBypass ? 'IPB Membership Confirmation Submitted' : 'Membership Confirmation Submitted');
      this.processing = false;
    },
    memberSurveyCompleteHandler() {
      this.creditGoalsSurveyAnswered = true;
      if (this.currentStage === STAGE.creditBand || this.creditBandVariant === 2) {
        this.creditBandAnswered = true;
      }
    },
    pollStatus(force = false, timeout = 3500) {
      if (this.decisionReady) {
        // console.log('decision already ready')
        this.stopPolling();
        return;
      }
      if (this.decisionPolling && !force) {
        // console.log('already polling')
        return;
      }
      this.decisionPolling = true;
      clearTimeout(this.decisionPollTimeout);
      this.decisionPollTimeout = setTimeout(() => {
        this.fetchStatus();
      }, timeout);
    },
    startPolling(delay = 0) {
      // console.log('polling start')
      this.pollStatus(false, delay);
    },
    stopPolling() {
      // console.log('polling stop')
      this.decisionPolling = false;
      clearTimeout(this.decisionPollTimeout);
      this.processing = false;
      this.processingReasonManual = null;
    },
    fetchStatus(handle = true) {
      if (this.alreadyHasSubscription) return;
      return this.$axios.$get('/api/members/status').then(data => {
        // console.log('poll', data)
        if (handle) {
          this.handleDecisionStatus(data);
        }
      }).catch(err => {
        var _a;
        console.error(`poll err MID ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id}`, err);
        this.stopPolling();
        this.errorContext = {
          context: 'status poll'
        };
        this.error = err;
      });
    },
    handleDecisionStatus(data) {
      var _a;
      this.decisionStatus = data.status;
      this.canRetryPlaid = data.can_retry_plaid;
      this.promptManualReview = data.prompt_manual_review;
      this.partialData = data.partial_data;
      this.plaidRefreshToken = data.refresh_token;
      // If the user has a lock, but also fails the plaid health check
      // let the lock state take precedence
      // This tends to primarily happen for folks who come back that were
      // deactivated
      if (!this.lock && (this.decisionStatus === 'error' || this.plaidRefreshToken)) {
        this.error = new Error('Plaid Item Backend Error');
        this.errorContext = {
          context: data.plaidError
        };
        console.log('decisionStatus = error for MID', (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id, data.plaidError);
      }
      if (this.plaidRemoteReady) {
        if (['approved', 'rejected', 'error'].includes(this.decisionStatus)) {
          this.stopPolling();
        } else {
          this.pollStatus(true);
        }
      }
    },
    async fetchKycStatus() {
      var _a;
      try {
        await this.fetchProfile();
        this.handleKycStatus();
      } catch (err) {
        console.error(`extra kyc poll err MID ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id}`, err);
        this.stopKycPolling();
        this.errorContext = {
          context: 'extra kyc status poll'
        };
        this.error = err;
      }
    },
    handleKycStatus() {
      var _a;
      const {
        kyc_complete,
        kyc_required_docs
      } = ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.document_portal) || {};
      const extraKycStatus = kyc_complete && !(Array.isArray(kyc_required_docs) ? kyc_required_docs.length : kyc_required_docs);
      if (extraKycStatus) {
        this.stopKycPolling();
        this.fetchProfile();
      }
    },
    startKycPolling(timeout) {
      this.kycStatusPoll(timeout);
    },
    kycStatusPoll(timeout) {
      var _a, _b;
      if ((_b = (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.document_portal) === null || _b === void 0 ? void 0 : _b.kyc_complete) {
        this.stopKycPolling();
        return;
      }
      if (this.kycStatusPolling) {
        return;
      }
      console.log('KYC Polling: ' + timeout);
      this.kycStatusPolling = true;
      clearTimeout(this.kycStatusPollTimeout);
      this.kycStatusPollTimeout = setTimeout(() => {
        this.fetchKycStatus();
      }, timeout);
    },
    stopKycPolling() {
      this.kycStatusPolling = false;
      clearTimeout(this.kycStatusPollTimeout);
      this.processing = false;
      this.processingReasonManual = null;
    },
    getEasStatus() {
      var _a;
      let easStatus = 'EAS_NotSubmitted'; // initial state
      const pendingKYC = this.forcePendingKyc || this.personaDeclined || this.personaFailed || this.kycComplete && this.kycStatus && (this.kycStatus !== 'PASSED' || !this.personaApproved) && !this.decisionFinalRejected && ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.document_portal.incomplete_locked_kyc);
      // In current flow if a member is rejected by our fraud engine an extra user will not
      // be created for them
      const isFraudRejectedMember = this.kycStatus === 'REJECTED' && !(this.memberProfile && this.memberProfile.extra_user_id);
      const isEcbsvRejected = this.ecbsvStatus === 'rejected';
      if (pendingKYC || isFraudRejectedMember) {
        if (this.kycStatus === 'REJECTED' && (!this.hashCollision || this.hashCollision && this.collisionAttempts > 2) || isEcbsvRejected) {
          easStatus = 'EAS_NoRetriesAvailable';
          this.warningContext = {
            context: 'EAS_NoRetriesAvailable'
          };
        } else if (this.kycStatus === 'REJECTED' && this.hashCollision && this.collisionAttempts === 2) {
          easStatus = 'EAS_LastRetryOffered';
          this.warningContext = {
            context: 'EAS_LastRetryOffered'
          };
        } else if (this.kycStatus === 'REJECTED' && this.hashCollision && this.collisionAttempts < 2) {
          easStatus = 'EAS_RetriesAvailable';
          this.warningContext = {
            context: 'EAS_RetriesAvailable'
          };
        } else {
          easStatus = 'EAS_Pending';
          this.warningContext = {
            context: 'EAS_Pending'
          };
        }
      } else {
        easStatus = 'EAS_NotSubmitted';
        // They have no pending KYC send no warnings ... they might not need to
        // this.warningContext = { context: "NotSubmitted" }
      }
      return easStatus;
    },
    submitPlaid() {
      var _a, _b, _c, _d;
      console.log('[plaid] submitting plaid for MID ', (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id);
      this.processingReasonManual = `Hold tight, we're connecting to ${((_c = (_b = this.plaidMetadata) === null || _b === void 0 ? void 0 : _b.institution) === null || _c === void 0 ? void 0 : _c.name) || 'your bank'}...`;
      this.processing = true;
      this.$axios.$post('/api/members/plaid', {
        public_token: this.plaidPublicToken,
        account_id: this.plaidAccountId,
        metadata: this.plaidMetadata,
        plan: this.planSelected ? this.plan : null,
        cohort: this.alternateFunnel,
        account_type_confirmed: this.plaidAccountType === 'individual',
        confirmed_account_access_token: (_d = this.corporateAccountInfo) === null || _d === void 0 ? void 0 : _d.access_token,
        forced_decision_result: this.isDev && this.decisionStatus
      }).then(async data => {
        var _a, _b, _c, _d, _e;
        const {
          insufficient,
          unsupported,
          corporate_account,
          bypassPlaidBalanceCheck
        } = data;
        if (unsupported) {
          window.extra_data_ul.log([{
            app: 'sales-funnel',
            kingdom: 'plaid_link_unsupported',
            phylum: (_b = (_a = this.plaidMetadata) === null || _a === void 0 ? void 0 : _a.institution) === null || _b === void 0 ? void 0 : _b.institution_id
          }]);
          this.processing = false;
          this.processingReasonManual = null;
          this.errorContext = {
            context: 'plaidUnsupported'
          };
          this.error = new Error('plaidUnsupported');
          console.log('[plaid] plaid link error: unsupported for MID', (_c = this.memberProfile) === null || _c === void 0 ? void 0 : _c.id);
          return;
        } else if (insufficient) {
          this.plaidInsufficient = data;
          console.log('[plaid] plaid insufficient for MID', (_d = this.memberProfile) === null || _d === void 0 ? void 0 : _d.id);
          this.processing = false;
          this.processingReasonManual = null;
          if (!bypassPlaidBalanceCheck) return;
        } else if (corporate_account) {
          this.plaidAccountType = 'corporate';
          this.corporateAccountInfo = corporate_account;
          console.log('[plaid] flagged corporate account for MID', (_e = this.memberProfile) === null || _e === void 0 ? void 0 : _e.id);
          this.processing = false;
          this.processingReasonManual = null;
          return;
        } else {
          // window.extra_data_ul.log([
          // 	{
          // 		app: 'sales-funnel',
          // 		kingdom: 'plaid_link_sufficient',
          // 		float1: balance,
          // 		float2: charge,
          // 	},
          // ])
        }
        if (this.memberProfile) this.memberProfile.bank_linked = true;
        await this.fetchProfile();
        await this.startPolling();
        setTimeout(() => {
          this.processingReasonManual = null;
        }, 300);
      }).catch(err => {
        var _a;
        this.errorContext = {
          context: err.error_code || 'submitPlaid'
        };
        this.error = err;
        console.error(`plaid error MID ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id}`, err);
        this.stopPolling();
        this.processingReasonManual = null;
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: 'plaid_link_try_again_offer'
        }]);
      });
      // .finally(() => {
      // 	// wait for decision
      // 	// this.processing = false
      // 	this.processingReasonManual = null
      // })
    },
    plaidIndividualAccountConfirmed() {
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'plaid_individual_account_confirm_btn'
      }]);
      this.plaidAccountType = 'individual';
      this.submitPlaid();
    },
    corporateAccountPlaidRetry() {
      var _a;
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'plaid_corporate_account_retry_btn'
      }]);
      this.$axios.$post('/api/members/plaid/delete', {
        public_token: this.plaidPublicToken,
        access_token: (_a = this.corporateAccountInfo) === null || _a === void 0 ? void 0 : _a.access_token
      }).then(() => {
        this.clearPlaidLocal();
      });
    },
    confirmBilling() {
      this.billingConfirmed = true;
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'billing_confirm_button',
        subclass: 'can_pick_annual',
        family: 'threshold_200'
      }]);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: '149_billing_confirmed'
      });
    },
    billingBack() {
      this.planSubmitted = false;
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'billing_back_button',
        subclass: 'can_pick_annual',
        family: 'threshold_200'
      }]);
    },
    resetPlaid() {
      // console.log('resetting plaid...')
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'plaid_reset'
      }]);
      this.processingReasonManual = null;
      this.processing = true;
      this.plaidProcessing = false;
      this.plaidAccessToken = null;
      this.plaidPublicToken = null;
      this.plaidAccountId = null;
      this.plaidMetadata = null;
      this.plaidRefreshToken = null;
      this.plaidInsufficient = null;
      this.plaidAccountType = null;
      this.corporateAccountInfo = null;
      this.warningContext = null;
      this.clearError();
      this.$axios.$post('/api/members/plaid/reset').then(data => {
        this.canRetryPlaid = data.can_retry_plaid;
      }).catch(err => {
        console.warn('plaid reset', err);
      }).finally(() => {
        this.openPlaid();
        // wait for the Plaid modal to open
        sleep(5000).then(() => this.processing = false);
      });
    },
    showPlaidRelinkErrorScreen(err = {}) {
      this.error = err;
      this.errorContext = {
        context: 'shouldRelinkPlaid'
      };
    },
    promptPlaidRelink() {
      this.resetPlaid();
      // makes sure if you close the Plaid modal you land on the Plaid screen (plaidRemoteReady === false)
      if (this.memberProfile) this.memberProfile.bank_linked = false;
    },
    formFillRetry() {
      this.formSubmitted = false;
      this.kycStatus = null;
      this.kycSubmitted = false;
      this.warningContext = null;
    },
    tryFinish() {
      // console.log('>>> try finish')
      if (!this.readyToSubmit) {
        console.warn('>>> not ready to submit');
        this.finishing = false;
        return;
      }
      if (this.decisionFinalRejected) {
        console.warn('>>> decision final rejected');
        this.finishing = false;
        return;
      }
      if (this.waitingForDecision) {
        console.warn('>>> decision not ready');
        this.processing = true;
        this.finishing = false;
        return;
      }
      this.finish();
    },
    async getFingerprints() {
      var _a;
      const start = dayjs();
      // eslint-disable-next-line new-cap
      window.sentijs = window.sentijs || new window.sentiJS('01ER09ACK1QVK2BP8F338865ER');
      const [sl, {
        sessionId: sc
      }] = await Promise.all([window.sentijs.getDevice({
        applicationId: `M${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id}`,
        firstName: this.form.first_name,
        lastName: this.form.last_name,
        email: this.form.email,
        phoneNumber: this.form.phone.replace(/[^\d+]/g, ''),
        ssn: this.form.ssn.replace(/[^\d+]/g, '')
      }), new Promise(resolve => window.devicer.run({
        publicKey: this.$config.environment === 'production' ? 'b15978b1-9366-44c9-9304-10d3de56a8fa' : '6649e227-15cb-4476-a9a3-15d60b6378b6',
        // endpoint: 'https://r.extra.app/api/ingestion',
        endpoint: 'https://dvnfo.com/api/ingestion',
        userConsent: true,
        context: 'signup'
      }, resolve))]);
      window.extra_data_ul.log([{
        app: 'sales-funnel',
        kingdom: 'fingerprint_fetched',
        float1: dayjs().diff(start, 'ms')
      }]);
      return {
        sl,
        sc
      };
    },
    async finish() {
      var _a;
      if (this.processing) {
        // console.log('already finishing')
        return;
      }
      if (this.isDemo) {
        // fake state for success screen
        this.clearError();
        this.billingConfirmed = true;
        this.formSubmitted = true;
        this.kycSubmitted = true;
        this.plaidAccountId = this.plaidAccountId || '123';
        this.plaidAccessToken = this.plaidAccessToken || this.plaidEnv;
        this.plan = this.plan || {
          billingCycle: 0,
          selectedPlan: 0
        };
        this.planSubmitted = true;
        this.kycStatus = 'PASSED';
        this.finishing = false;
        return;
      }
      // console.log('>>> finish')
      this.processing = true;
      // NOTE: the LOC agreement slug is dependent on the user's (geographic) state, this is handled in /finish
      this.formPolicyConsentTimestamps.lead_loc_agreement = this.formPolicyConsentTimestamps.extra_credit_notice;
      // fetch fingerprints
      let fingerprints;
      try {
        fingerprints = await this.getFingerprints();
      } catch (err) {
        console.error('[finish.fingerprints]', err);
      }
      this.$axios.$post('/api/members/finish', {
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        email: this.form.email,
        address: this.form.address,
        plan: this.plan,
        plan_autopay: this.planAutopay,
        payment_option: this.formPaymentOptionEnabled ? this.paymentOption : undefined,
        // plaid_public_token: this.plaidPublicToken,
        // plaid_access_token: this.plaidAccessToken,
        // plaid_account_id: this.plaidAccountId,
        fingerprints,
        date_of_birth: this.formattedBirthday,
        ssn: this.form.ssn.replace(/[^\d+]/g, ''),
        policies: this.formPolicyConsentTimestamps,
        force_fraud_result: this.isDev && (this.forcedFraudResult || window._force_kyc),
        force_ecbsv_result: this.isDev && (this.forcedECBSVResult || window._force_ecbsv),
        issuing_bank: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.issuing_bank // for data integrity
      }).then(async data => {
        // console.log('Finish then', data)
        var _a, _b;
        await this.fetchProfile();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '189_kyc_success'
        });
        const purchase = data && data.purchase;
        const trace = data && data.trace;
        this.traceId = trace;
        this.ecbsvStatus = data.ecbsvHardRejection ? 'rejected' : 'approved';
        if (data.cleanFunnelResult) {
          this.cleanFunnel = true;
          this.$analyticsHub.track('funnel.EAS_Form_Submit', {
            event_datetime: new Date().toISOString(),
            fca_status: 'clean pass',
            member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id
          });
        } else {
          this.$analyticsHub.track('funnel.EAS_Form_Submit', {
            event_datetime: new Date().toISOString(),
            fca_status: 'flagged applicant',
            member_id: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.id
          });
        }
        this.finishCallback({
          purchase,
          err: null,
          trace
        });
      }).catch(err => {
        var _a, _b, _c;
        console.error(`Finish err MID ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id}`, err, err.response.data);
        const r = err && err.response ? err.response.data : {};
        const purchase = r.purchase;
        const trace = r.trace;
        if (r.promptECBSV) {
          this.promptECBSV = true;
          this.finishing = false;
          this.processing = false;
          window.extra_data_ul.log([{
            app: 'sales-funnel',
            kingdom: 'finish',
            phylum: 'fail',
            class: 'ecbsv-requested'
          }]);
          this.$analyticsHub.track('funnel.Finish_eCBSV_Requested', {
            event_datetime: new Date().toISOString(),
            member_id: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.id
          });
          return;
        }
        if (r.ecbsvResubmit) {
          this.ecbsvInvalid = true;
          this.finishing = false;
          this.processing = false;
          window.extra_data_ul.log([{
            app: 'sales-funnel',
            kingdom: 'finish',
            phylum: 'fail',
            class: 'ecbsv-invalid'
          }]);
          this.$analyticsHub.track('funnel.Finish_eCBSV_Resubmit', {
            event_datetime: new Date().toISOString(),
            member_id: (_c = this.memberProfile) === null || _c === void 0 ? void 0 : _c.id
          });
          return;
        }
        if (r.invalidEmail) {
          this.finishing = false;
          this.processing = false;
          this.formSubmitted = false;
          this.formShowEmailTooltip = true;
          return;
        }
        // setTimeout(() => {
        // 	this.$scrollTo('#ecbsv-scroll')
        // }, 50)
        // reload doesnt throw an error screen, but just reloads the profile state
        this.finishCallback({
          purchase,
          err,
          showError: !r.reload,
          errorContextOverride: r.nonPersistentPlaidError && 'finishPlaidHealthError' || r.poBox && 'finishPoBox' || r.collision && 'finishCollision' || r.decisionPending && 'decisionPending' || err.response.data.error,
          trace
        });
        if (r.reload !== false) {
          this.fetchProfile();
        }
      }).finally(() => {
        // this.processing = false
        this.finishing = false;
      });
      // update formSubmissionStatus state
      this.formSubmittedState();
    },
    async handleECBSVAgreed(isChecked) {
      var _a;
      console.log('eCBSV Agreement: ', isChecked);
      this.formPolicyConsentTimestamps.ecbsv_authorization = new Date();
      // modified formPolicies to include ecbsv_authorization
      const newPolicies = [...this.formPolicies];
      if (isChecked) {
        if (!newPolicies.includes('ecbsv_authorization')) {
          newPolicies.push('ecbsv_authorization');
        }
        this.$analyticsHub.track('funnel.Policy_eCBSV_Agreement_Signed', {
          event_datetime: this.formPolicyConsentTimestamps.ecbsv_authorization.toISOString(),
          member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id
        });
      }
      // trigger setter with new policy
      this.formPolicies = newPolicies;
      await this.resubmit();
    },
    async resubmit() {
      var _a, _b;
      if (this.processing) {
        // console.log('already finishing')
        return;
      }
      this.promptECBSV = false;
      if (!this.readyToSubmit) {
        console.warn('>>> not ready to submit');
        this.finishing = false;
        return;
      }
      console.log('>>> resubmit');
      this.$analyticsHub.track('funnel.eCBSV_Auto_Resubmit', {
        event_datetime: new Date().toISOString(),
        member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id
      });
      this.processing = true;
      let fingerprints;
      try {
        fingerprints = await this.getFingerprints();
      } catch (err) {
        console.error('[finish.fingerprints]', err);
      }
      this.$axios.$post('/api/members/resubmit', {
        first_name: this.form.first_name,
        last_name: this.form.last_name,
        email: this.form.email,
        address: this.form.address,
        plan: this.plan,
        plan_autopay: this.planAutopay,
        payment_option: this.formPaymentOptionEnabled ? this.paymentOption : undefined,
        // plaid_public_token: this.plaidPublicToken,
        // plaid_access_token: this.plaidAccessToken,
        // plaid_account_id: this.plaidAccountId,
        fingerprints,
        date_of_birth: this.formattedBirthday,
        ssn: this.form.ssn.replace(/[^\d+]/g, ''),
        policies: this.formPolicyConsentTimestamps,
        force_fraud_result: this.isDev && (this.forcedFraudResult || window._force_kyc),
        force_ecbsv_result: this.isDev && (this.forcedECBSVResult || window._force_ecbsv),
        issuing_bank: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.issuing_bank // for data integrity
      }).then(async data => {
        var _a, _b;
        await this.fetchProfile();
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '189_kyc_success'
        });
        const purchase = data && data.purchase;
        const trace = data && data.trace;
        this.traceId = trace;
        this.ecbsvStatus = data.ecbsvHardRejection ? 'rejected' : 'approved';
        if (data.cleanFunnelResult) {
          this.cleanFunnel = true;
          this.$analyticsHub.track('funnel.EAS_Form_Submit', {
            event_datetime: new Date().toISOString(),
            fca_status: 'clean pass',
            member_id: (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id
          });
        } else {
          this.$analyticsHub.track('funnel.EAS_Form_Submit', {
            event_datetime: new Date().toISOString(),
            fca_status: 'flagged applicant',
            member_id: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.id
          });
        }
        this.finishCallback({
          purchase,
          err: null,
          trace
        });
      }).catch(err => {
        var _a, _b, _c;
        console.error(`Finish err MID ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id}`, err, err.response.data);
        const r = err && err.response ? err.response.data : {};
        const purchase = r.purchase;
        const trace = r.trace;
        if (r.promptECBSV) {
          this.promptECBSV = true;
          this.finishing = false;
          this.processing = false;
          window.extra_data_ul.log([{
            app: 'sales-funnel',
            kingdom: 'finish',
            phylum: 'fail',
            class: 'ecbsv-requested'
          }]);
          this.$analyticsHub.track('funnel.Prompting_eCBSV', {
            event_datetime: new Date().toISOString(),
            member_id: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.id
          });
          return;
        }
        if (r.ecbsvResubmit) {
          this.ecbsvInvalid = true;
          this.finishing = false;
          this.processing = false;
          window.extra_data_ul.log([{
            app: 'sales-funnel',
            kingdom: 'finish',
            phylum: 'fail',
            class: 'ecbsv-invalid'
          }]);
          this.$analyticsHub.track('funnel.eCBSV_Resubmit_Requested', {
            event_datetime: new Date().toISOString(),
            member_id: (_c = this.memberProfile) === null || _c === void 0 ? void 0 : _c.id
          });
          return;
        }
        if (r.invalidEmail) {
          this.finishing = false;
          this.processing = false;
          this.formSubmitted = false;
          this.formShowEmailTooltip = true;
          return;
        }
        // setTimeout(() => {
        // 	this.$scrollTo('#ecbsv-scroll')
        // }, 50)
        // reload doesnt throw an error screen, but just reloads the profile state
        this.finishCallback({
          purchase,
          err,
          showError: !r.reload,
          errorContextOverride: r.nonPersistentPlaidError && 'finishPlaidHealthError' || r.poBox && 'finishPoBox' || r.collision && 'finishCollision' || r.decisionPending && 'decisionPending' || err.response.data.error,
          trace
        });
        if (r.reload !== false) {
          this.fetchProfile();
        }
      }).finally(() => {
        // this.processing = false
        this.finishing = false;
      });
      // update formSubmissionStatus state
      this.formSubmittedState();
    },
    finishCallback({
      purchase,
      err,
      showError = true,
      errorContextOverride,
      trace,
      skips = {}
    }) {
      // console.log('FINISH CALLBACK', { purchase, err })
      var _a;
      const upfrontPrice = this.upfrontPrice;
      skips = skips || {};
      this.purchase = purchase;
      // not rejected, no error = success
      if (purchase) {
        setTimeout(() => {
          // console.log('FINISH CALLBACK - PURCHASE TRIGGERED')
          var _a, _b, _c, _d, _e;
          this.$analyticsHub.identify((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id, {
            insufficient: !!((_b = this.plaidInsufficient) === null || _b === void 0 ? void 0 : _b.insufficient)
          });
          if (window.branch) {
            window.branch.setIdentity(window.g_user_tag);
            window.branch.logEvent('extra_user_created', {
              user_tag: window.g_user_tag
            },
            // @ts-ignore
            err => {
              if (err) {
                this.$sentry.captureException(err);
              }
            });
          }
          let eventName;
          const eventID = uuidV4();
          const eventParams = this.plan ? {
            currency: 'USD',
            value: this.upfrontPrice
          } : {};
          if (((_c = this.plaidInsufficient) === null || _c === void 0 ? void 0 : _c.insufficient) && ((_d = this.plaidInsufficient) === null || _d === void 0 ? void 0 : _d.bypassPlaidBalanceCheck)) {
            eventName = 'PurchasePending';
            this.$fb.query('trackCustom', eventName, eventParams, {
              eventID
            });
          } else {
            eventName = 'Purchase';
            const fbArgs = [eventName, {
              ...eventParams
            }, {
              eventID
            }];
            this.$fb.track(...fbArgs);
            // CAPI happens below in the call to the confirmPlan function with POST /api/members/confirm-cardholder
            console.log('sending FB Purchase:', fbArgs);
          }
          // CAPI for Purchase moved here from helpers.js
          this.$axios.$post('/api/fbevent', {
            // CAPI
            eventName,
            member: {
              ...this.memberProfile,
              external_id: this.$cookies.get('ajs_anonymous_id')
            },
            eventParams: {
              custom_data: eventParams
            },
            eventId: eventID
          }).catch(err => {
            var _a;
            console.log(`[fbEvent] ${eventName} CAPI failed for mid: ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id} with error: ${err}`);
          });
          // if (!skips.adwords) {
          // 	this.$gtag(
          // 		'event',
          // 		'conversion',
          // 		this.plan && {
          // 			send_to: 'AW-429071169/Hq2sCK3DsfUBEMG2zMwB',
          // 			value: upfrontPrice,
          // 			currency: 'USD',
          // 			transaction_id: this.memberProfile.id,
          // 		}
          // 	)
          // }
          window.extra_data_ul.log([{
            app: 'sales-funnel',
            kingdom: 'finish',
            phylum: 'success'
          }]);
          this.updateIdentity();
          if (!skips.gtm) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: '190_purchase_pixel',
              price: upfrontPrice,
              order_id: `trace_${trace}`,
              member_id: (_e = this.memberProfile) === null || _e === void 0 ? void 0 : _e.id,
              plan_cycle: this.encodedCycle,
              plan_type: this.encodedPlan,
              first_name: this.form.first_name,
              last_name: this.form.last_name,
              address_street_one: this.form.address.street_one,
              address_street_two: this.form.address.street_two,
              address_zip_code: this.form.address.zip_code,
              address_state: this.form.address.state,
              address_city: this.form.address.city,
              date_of_birth: this.formattedBirthday
            });
          }
          // console.log(datadogLogs, datadogLogs.logger, datadogLogs.logger.info)
          // const a = datadogLogs.logger.info('post verizon', {
          // 	len: window.dotq && window.dotq.length,
          // 	dotq: window.dotq,
          // 	cookies: document.cookie,
          // })
          // console.log({ a })
          // console.log('post verizon', window.dotq && window.dotq.length, window.dotq)
        }, 500);
      }
      // error or rejected
      else {
        // console.log('FINISH CALLBACK - PURCHASE NOT TRIGGERED')
        window.extra_data_ul.log([{
          app: 'sales-funnel',
          kingdom: 'finish',
          phylum: 'fail',
          class: !purchase ? 'not-sale' : err ? 'error' : 'unknown',
          subclass: err && err.response && err.response.data && err.response.data.trace && 'trace_' + err.response.data.trace
        }]);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: '188_kyc_error'
        });
        if (err && showError) {
          // kick to /error
          this.errorContext = {
            context: errorContextOverride || 'finish',
            trace: err && err.response && err.response.data && (parseInt(err.response.data.error) ? err.response.data.error : err.response.data.trace)
          };
          this.error = err;
        }
      }
      // FCA Conditional Authorization
      if (this.cleanFunnel && this.conditionalAuthEnabled) {
        try {
          this.confirmPlan(true);
        } catch (err) {
          console.error(err);
          throw new Error(`confirmPlan error for clean pass MID ${(_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id} ${err}`);
        }
      }
    },
    async onPersonaComplete() {
      this.forcePendingKyc = true;
      this.processing = true;
      this.processingReasonManual = 'Hang tight, we are processing your application...';
      await this.$axios.$get('/api/members/update-signup-cache-kyc');
      const timeout = 10000;
      this.startKycPolling(timeout);
      setTimeout(() => {
        this.forcePendingKyc = false;
      }, timeout);
    },
    instagramPrompt() {
      var _a, _b;
      const handle = prompt('Enter your IG handle:', ((_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.insta_handle) || '') || undefined;
      if (this.memberProfile) this.memberProfile.insta_handle = handle;
      if (handle) {
        this.$axios.$put('/api/members/profile/insta', {
          insta_handle: (_b = this.memberProfile) === null || _b === void 0 ? void 0 : _b.insta_handle
        }).then(() => {
          alert("Awesome! We'll approve your follow soon!");
        }).catch(err => {
          alert('Something went wrong... Try again\n' + err);
          this.$sentry.captureException(err);
        });
      }
      this.$forceUpdate();
    },
    onSectionError(section, error) {
      var _a;
      this.errorContext = {
        context: section + 'SectionError'
      };
      console.error(`${section} section error for MID`, (_a = this.memberProfile) === null || _a === void 0 ? void 0 : _a.id);
      this.error = error;
    },
    clearError() {
      this.errorContext = null;
      this.error = null;
      this.finishing = false;
      // window.location.reload()
    },
    reloadPage() {
      window.location.reload();
    },
    getPlaidLogo(memberProfile) {
      return memberProfile.bank_account.institution_id || memberProfile.bank_account.institution.institution_id ? `https://api.extra.app/v1/misc/institutions/${memberProfile.bank_account.institution_id || memberProfile.bank_account.institution.institution_id}/logo` : memberProfile.bank_account.institution.logo ? `data:image/png;base64,${memberProfile.bank_account.institution.logo}` : null;
    },
    formatUSD(value) {
      if (typeof value !== 'number') {
        return '$?';
      }
      return [value < 0 ? '-' : '', '$', Math.abs(value)].join('');
    },
    // promo code entry form on success screen
    submitPromoCode() {
      this.codeSent = true;
      if (this.promoCodeValue === 'EXTRA10K' || this.promoCodeValue === 'EXTRA30K') {
        alert('This offer has expired');
        this.codeSent = false;
        return;
      }
      this.$axios.$post('/api/members/redeem', {
        promoCode: this.promoCodeValue
      }).then(() => {
        this.code_redeemed = true;
        alert('Awesome! Check the app, your points will be applied to your account shortly!');
      }).catch(err => {
        this.showError = err.response.data.message;
        alert(`Hmm that code doesn't look right... Try again! \n ${this.showError}`);
        console.warn(err);
        this.codeSent = false;
      });
    },
    formSubmittedState() {
      this.$store.commit('formSubmitted');
    }
  }
});